import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { agentApi } from "../../lib/apiClient";
import { canAccessPath } from "../../lib/agencyAccess";
import { asArray, unwrapData } from "../../lib/catalog";
import { searchablePages } from "../../data/mockData";
import { formatBookingStatus } from "../../lib/bookingStatus";

const RESULT_LIMIT = 5;
const MIN_QUERY_LENGTH = 2;

const emptyResults = {
  pages: [],
  bookings: [],
  customers: [],
  packages: [],
  hotels: [],
};

function matchPages(query, agencyRole) {
  const term = query.trim().toLowerCase();
  if (!term) return [];
  return searchablePages.filter((page) => {
    if (!canAccessPath(page.path, agencyRole)) return false;
    const haystack = `${page.label} ${page.path}`.toLowerCase();
    return haystack.includes(term);
  });
}

function customerName(customer) {
  return `${customer.cust_name || ""} ${customer.cust_last_name || ""}`.trim() || "Customer";
}

function packageName(pkg) {
  return pkg.name || pkg.u_pkg_tour_name || pkg.code || pkg.u_pkg_code || "Package";
}

function packagePath(pkg) {
  return `/packages/${pkg.slug || pkg.u_pkg_tour_name_slugs || pkg.id || pkg.u_pkg_id}`;
}

function hotelPath(hotel) {
  return `/hotels/${hotel.slug || hotel.id}`;
}

function flattenResults(results) {
  return [
    ...results.pages.map((item) => ({ group: "pages", to: item.path, item })),
    ...results.bookings.map((item) => ({ group: "bookings", to: `/bookings/${item.booking_id}`, item })),
    ...results.customers.map((item) => ({
      group: "customers",
      to: `/customers?q=${encodeURIComponent(customerName(item))}`,
      item,
    })),
    ...results.packages.map((item) => ({ group: "packages", to: packagePath(item), item })),
    ...results.hotels.map((item) => ({ group: "hotels", to: hotelPath(item), item })),
  ];
}

function ResultRow({ to, icon, title, subtitle, active, onSelect }) {
  return (
    <Link
      to={to}
      className={`navbar-search-item${active ? " is-active" : ""}`}
      onClick={onSelect}
    >
      <span className="navbar-search-item-icon" aria-hidden="true">
        <iconify-icon icon={icon}></iconify-icon>
      </span>
      <span className="navbar-search-item-copy min-w-0">
        <span className="navbar-search-item-title">{title}</span>
        {subtitle ? <span className="navbar-search-item-subtitle">{subtitle}</span> : null}
      </span>
    </Link>
  );
}

function ResultGroup({ title, children, moreTo, moreLabel, onSelect }) {
  if (!children) return null;
  return (
    <div className="navbar-search-group">
      <div className="navbar-search-group-title">{title}</div>
      {children}
      {moreTo ? (
        <Link to={moreTo} className="navbar-search-more" onClick={onSelect}>
          {moreLabel}
        </Link>
      ) : null}
    </div>
  );
}

export default function HeaderSearch({ agencyRole = "" }) {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);
  const requestIdRef = useRef(0);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [results, setResults] = useState(emptyResults);

  const items = useMemo(() => flattenResults(results), [results]);
  const trimmed = query.trim();
  const showPanel = open && trimmed.length > 0;

  useEffect(() => {
    setOpen(false);
    setQuery("");
    setResults(emptyResults);
    setActiveIndex(0);
  }, [location.pathname, location.search]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const pages = matchPages(trimmed, agencyRole);
    if (trimmed.length < MIN_QUERY_LENGTH) {
      requestIdRef.current += 1;
      setResults({ ...emptyResults, pages });
      setLoading(false);
      setActiveIndex(0);
      return undefined;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setResults({ ...emptyResults, pages });
    setLoading(true);

    const timer = window.setTimeout(async () => {
      const params = { q: trimmed, search: trimmed, limit: RESULT_LIMIT, page: 1 };
      const [bookingsResult, customersResult, packagesResult, hotelsResult] = await Promise.allSettled([
        agentApi.get("/travel-partner/bookings", { params }),
        agentApi.get("/travel-partner/customers", { params }),
        agentApi.get("/umrah-packages/search", { params: { q: trimmed, limit: RESULT_LIMIT, audience: "agent" } }),
        agentApi.get("/umrah-hotels/search", { params: { q: trimmed, limit: RESULT_LIMIT } }),
      ]);

      if (requestId !== requestIdRef.current) return;

      setResults({
        pages: matchPages(trimmed, agencyRole),
        bookings: bookingsResult.status === "fulfilled" ? asArray(unwrapData(bookingsResult.value)).slice(0, RESULT_LIMIT) : [],
        customers: customersResult.status === "fulfilled" ? asArray(unwrapData(customersResult.value)).slice(0, RESULT_LIMIT) : [],
        packages: packagesResult.status === "fulfilled" ? asArray(unwrapData(packagesResult.value)).slice(0, RESULT_LIMIT) : [],
        hotels: hotelsResult.status === "fulfilled" ? asArray(unwrapData(hotelsResult.value)).slice(0, RESULT_LIMIT) : [],
      });
      setActiveIndex(0);
      setLoading(false);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [trimmed, agencyRole]);

  function closeSearch() {
    setOpen(false);
  }

  function goTo(path) {
    if (!path) return;
    closeSearch();
    setQuery("");
    navigate(path);
  }

  function handleSubmit(event) {
    event.preventDefault();
    if (items[activeIndex]) {
      goTo(items[activeIndex].to);
      return;
    }
    if (trimmed) {
      goTo(`/bookings?q=${encodeURIComponent(trimmed)}`);
    }
  }

  function handleKeyDown(event) {
    if (!showPanel) return;

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((value) => (items.length ? (value + 1) % items.length : 0));
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((value) => (items.length ? (value - 1 + items.length) % items.length : 0));
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setOpen(false);
    }
  }

  let runningIndex = -1;
  function nextIndex() {
    runningIndex += 1;
    return runningIndex;
  }

  return (
    <form className="navbar-search" onSubmit={handleSubmit} ref={rootRef} role="search">
      <div className="navbar-search-field">
        <span className="navbar-search-icon" aria-hidden="true">
          <iconify-icon icon="solar:magnifer-linear"></iconify-icon>
        </span>
        <input
          type="search"
          name="search"
          placeholder="Search"
          value={query}
          autoComplete="off"
          aria-label="Search bookings, customers, packages, and pages"
          aria-expanded={showPanel}
          aria-controls="agent-header-search-results"
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </div>
      {showPanel ? (
        <div id="agent-header-search-results" className="navbar-search-results" role="listbox">
          {loading ? <div className="navbar-search-status">Searching...</div> : null}

          {!loading && trimmed.length < MIN_QUERY_LENGTH && results.pages.length === 0 ? (
            <div className="navbar-search-status">Type at least 2 characters to search records.</div>
          ) : null}

          {!loading && trimmed.length >= MIN_QUERY_LENGTH && items.length === 0 ? (
            <div className="navbar-search-status">No matches for “{trimmed}”.</div>
          ) : null}

          {results.pages.length > 0 ? (
            <ResultGroup title="Pages">
              {results.pages.map((page) => {
                const index = nextIndex();
                return (
                  <ResultRow
                    key={page.path}
                    to={page.path}
                    icon={page.icon}
                    title={page.label}
                    subtitle={page.path}
                    active={index === activeIndex}
                    onSelect={closeSearch}
                  />
                );
              })}
            </ResultGroup>
          ) : null}

          {results.bookings.length > 0 ? (
            <ResultGroup
              title="Bookings"
              moreTo={`/bookings?q=${encodeURIComponent(trimmed)}`}
              moreLabel="See all bookings"
              onSelect={closeSearch}
            >
              {results.bookings.map((booking) => {
                const index = nextIndex();
                return (
                  <ResultRow
                    key={booking.booking_id}
                    to={`/bookings/${booking.booking_id}`}
                    icon="solar:calendar-outline"
                    title={booking.booking_reference || `Booking ${booking.booking_id}`}
                    subtitle={[booking.customer_name, booking.package_name, formatBookingStatus(booking.booking_status)]
                      .filter(Boolean)
                      .join(" · ")}
                    active={index === activeIndex}
                    onSelect={closeSearch}
                  />
                );
              })}
            </ResultGroup>
          ) : null}

          {results.customers.length > 0 ? (
            <ResultGroup
              title="Customers"
              moreTo={`/customers?q=${encodeURIComponent(trimmed)}`}
              moreLabel="See all customers"
              onSelect={closeSearch}
            >
              {results.customers.map((customer) => {
                const index = nextIndex();
                const name = customerName(customer);
                return (
                  <ResultRow
                    key={customer.cust_id}
                    to={`/customers?q=${encodeURIComponent(name)}`}
                    icon="solar:user-id-outline"
                    title={name}
                    subtitle={[customer.cust_mobile, customer.cust_email].filter(Boolean).join(" · ")}
                    active={index === activeIndex}
                    onSelect={closeSearch}
                  />
                );
              })}
            </ResultGroup>
          ) : null}

          {results.packages.length > 0 ? (
            <ResultGroup
              title="Packages"
              moreTo={`/packages?q=${encodeURIComponent(trimmed)}`}
              moreLabel="See all packages"
              onSelect={closeSearch}
            >
              {results.packages.map((pkg) => {
                const index = nextIndex();
                const id = pkg.id || pkg.u_pkg_id || packageName(pkg);
                return (
                  <ResultRow
                    key={id}
                    to={packagePath(pkg)}
                    icon="solar:box-linear"
                    title={packageName(pkg)}
                    subtitle={[pkg.code || pkg.u_pkg_code, pkg.duration || pkg.u_pkg_duration].filter(Boolean).join(" · ")}
                    active={index === activeIndex}
                    onSelect={closeSearch}
                  />
                );
              })}
            </ResultGroup>
          ) : null}

          {results.hotels.length > 0 ? (
            <ResultGroup
              title="Hotels"
              moreTo={`/hotels?q=${encodeURIComponent(trimmed)}`}
              moreLabel="See all hotels"
              onSelect={closeSearch}
            >
              {results.hotels.map((hotel) => {
                const index = nextIndex();
                return (
                  <ResultRow
                    key={hotel.id || hotel.slug}
                    to={hotelPath(hotel)}
                    icon="solar:buildings-2-linear"
                    title={hotel.name || "Hotel"}
                    subtitle={[hotel.code, hotel.address].filter(Boolean).join(" · ")}
                    active={index === activeIndex}
                    onSelect={closeSearch}
                  />
                );
              })}
            </ResultGroup>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}
